import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SUPPORTED_LANGS = ['ru', 'uz'];

const setMetaTag = (attr, key, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const applyTitle = (title) => {
  document.title = title;
  // Re-apply after a short delay to override any external scripts
  setTimeout(() => { document.title = title; }, 100);
  setTimeout(() => { document.title = title; }, 500);
};

const detectLang = (contextLang) => {
  // Priority: URL prefix > context language
  if (typeof window !== 'undefined') {
    const seg = window.location.pathname.split('/').filter(Boolean)[0];
    if (SUPPORTED_LANGS.includes(seg)) return seg;
  }
  if (contextLang && SUPPORTED_LANGS.includes(contextLang)) return contextLang;
  return null; // English (base)
};

export const useSEO = (slug, defaults = {}) => {
  const [seo, setSeo] = useState(null);
  const { language } = useLanguage();
  const defaultTitle = defaults.title;
  const defaultDescription = defaults.description;
  const defaultKeywords = defaults.keywords;

  useEffect(() => {
    const load = async () => {
      const lang = detectLang(language);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/seo/${slug}`);
        const data = res.data;
        setSeo(data);

        // Locale-aware field selection: title_ru, description_ru, keywords_ru (or _uz)
        // Falls back to base English fields if no locale-specific value is set.
        const localeTitle = lang ? data?.[`title_${lang}`] : null;
        const localeDesc = lang ? data?.[`description_${lang}`] : null;
        const localeKeywords = lang ? data?.[`keywords_${lang}`] : null;

        const title = localeTitle || data?.title || defaultTitle || '';
        const desc = localeDesc || data?.description || defaultDescription || '';
        const keywords = localeKeywords || data?.keywords || defaultKeywords || '';
        const ogImage = data?.ogImage || '';

        if (title) {
          applyTitle(title);
          setMetaTag('property', 'og:title', title);
          setMetaTag('name', 'twitter:title', title);
        }
        if (desc) {
          setMetaTag('name', 'description', desc);
          setMetaTag('property', 'og:description', desc);
          setMetaTag('name', 'twitter:description', desc);
        }
        if (keywords) setMetaTag('name', 'keywords', keywords);
        if (ogImage) {
          setMetaTag('property', 'og:image', ogImage);
          setMetaTag('name', 'twitter:image', ogImage);
        }

        // Update <html lang> attribute to match active locale
        if (typeof document !== 'undefined') {
          document.documentElement.setAttribute('lang', lang || 'en');
        }
      } catch (e) {
        // On error, apply defaults
        if (defaultTitle) applyTitle(defaultTitle);
        if (defaultDescription) setMetaTag('name', 'description', defaultDescription);
        if (defaultKeywords) setMetaTag('name', 'keywords', defaultKeywords);
      }
    };
    load();
  }, [slug, language, defaultTitle, defaultDescription, defaultKeywords]);

  return seo;
};
